import { useMutation, UseMutationOptions, useQueryClient } from "@tanstack/react-query";
import useApi from "../../../services/auth/useApi";
import { AxiosError, isAxiosError } from "axios";
import { spaceQueryKeys } from "../queries";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { SpaceIdType } from "../../../types/space";

import { SpaceStatus } from "../../../types/space/enum";
import { inboxQueryKeys } from "../../inbox/queries";
import { InboxBlockType } from "../../../types/block/BlockType";
import { BlockType } from "../../../types/block/type";
import { logger } from "../../../utils/logger";
import {
  inProgressSpaceListState,
  noSpaceIdSelector,
} from "../../../recoil/spaces/inProgressSpaceListState";
import { debounceInvalidate } from "../../../services/optimistic-updates/debounceInvalidate";
import { usePreference } from "../../../hooks/preference/usePreference";
export const trashSpaceApi = async (
  api: ReturnType<typeof useApi>,
  projectId: SpaceIdType
): Promise<void> => {
  await api.patch(`/spaces/${projectId}`, {
    itemStatus: SpaceStatus.Deleted,
  });
};

export const useMoveToTrashSpaceMutation = (
  options?: UseMutationOptions<void, AxiosError, SpaceIdType>
) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const noSpaceId = useRecoilValue(noSpaceIdSelector);
  const setInProgressSpaceList = useSetRecoilState(inProgressSpaceListState);

  const { preference, updatePreference } = usePreference();

  return useMutation<void, AxiosError, SpaceIdType>({
    mutationFn: (spaceId: SpaceIdType) => trashSpaceApi(api, spaceId),
    onMutate: async (spaceId) => {
      await queryClient.cancelQueries({ queryKey: spaceQueryKeys.status(SpaceStatus.InProgress) });
      await queryClient.cancelQueries({
        queryKey: spaceQueryKeys.status(SpaceStatus.Completed),
      });
      await queryClient.cancelQueries({
        queryKey: spaceQueryKeys.status(SpaceStatus.Deleted),
      });

      // general
      queryClient.setQueryData(inboxQueryKeys.inboxGeneral(), (prev: BlockType[]) => {
        if (!prev) return [];
        return prev.map((block) => {
          if (block.spaceId === spaceId) {
            return { ...block, spaceId: noSpaceId };
          }
          return block;
        });
      });

      // todayGeneral
      queryClient.setQueryData(inboxQueryKeys.todayGeneral(), (prev: InboxBlockType[]) => {
        if (!prev) return [];
        return prev.map((block) => {
          if (block.spaceId === spaceId) {
            return { ...block, spaceId: noSpaceId };
          }
          return block;
        });
      });

      // overdue
      queryClient.setQueryData(inboxQueryKeys.inboxOverdue(), (prev: InboxBlockType[]) => {
        if (!prev) return [];
        return prev.map((block) => {
          if (block.spaceId === spaceId) {
            return { ...block, spaceId: noSpaceId };
          }
          return block;
        });
      });

      // completed
      queryClient.setQueryData(inboxQueryKeys.inboxCompleted(), (prev: BlockType[]) => {
        if (!prev) return [];
        return prev.map((block) => {
          if (block.spaceId === spaceId) {
            return { ...block, spaceId: noSpaceId };
          }
          return block;
        });
      });
      // NOTE: return 값 쓰이는 곳 없음
    },

    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: spaceQueryKeys.all });
      // inbox는 recoil state가 없어요
      debounceInvalidate.enqueueInvalidate(inboxQueryKeys.all());

      // NOTE 삭제 시 spaceSidebar에서 사라짐
      setInProgressSpaceList((prev) => prev?.filter((space) => space.id !== id) ?? []);

      updatePreference({
        selectedSpaceIds: preference?.selectedSpaceIds?.filter((spaceId) => spaceId !== id),
      });
    },
    onError: (error) => {
      if (isAxiosError(error)) {
        queryClient.invalidateQueries({ queryKey: spaceQueryKeys.all });
        debounceInvalidate.enqueueInvalidate(inboxQueryKeys.all(), {
          shouldInvalidateImmediately: true,
        });
        logger.error(error);
      }
    },
    ...options,
  });
};
